import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { canSee, homeFor } from "@/lib/sections";
import Login from "@/pages/Login";
import OperatorEntry from "@/pages/OperatorEntry";
import OperatorHistory from "@/pages/OperatorHistory";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminControl from "@/pages/AdminControl";
import RentalsPage from "@/pages/RentalsPage";
import TelepassPage from "@/pages/TelepassPage";
import PhonesPage from "@/pages/PhonesPage";
import AssignmentsPage from "@/pages/AssignmentsPage";
import TollsPage from "@/pages/TollsPage";
import TyresPage from "@/pages/TyresPage";
import ForecastPage from "@/pages/ForecastPage";
import FormsPage from "@/pages/FormsPage";
import WashesPage from "@/pages/WashesPage";
import AdBluePage from "@/pages/AdBluePage";
import PublicFormPage from "@/pages/PublicFormPage";
import LogbookPage from "@/pages/LogbookPage";
import RequestsPage from "@/pages/RequestsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import AiAssistantPage from "@/pages/AiAssistantPage";
import AdminStations from "@/pages/AdminStations";
import AdminUsers from "@/pages/AdminUsers";
import FleetPage from "@/pages/FleetPage";
import DamagesPage from "@/pages/DamagesPage";

const NoSections = () => <div className="py-20 text-center text-slate-500" data-testid="no-sections">Nessuna sezione abilitata per il tuo utente. Contatta l'amministratore.</div>;

const Protected = ({ role, section, full, children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Caricamento...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  if (section && !canSee(user, section)) return <Navigate to={homeFor(user)} replace />;
  if (full && user.supervisor) return <Navigate to="/admin" replace />;
  return <AppLayout>{children}</AppLayout>;
};

const Home = () => {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
};
import AuthCallback from "@/pages/AuthCallback";
import AdminImport from "@/pages/AdminImport";

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/stazione" element={<Protected role="operator" section="entry"><OperatorEntry /></Protected>} />
          <Route path="/stazione/storico" element={<Protected role="operator" section="history"><OperatorHistory /></Protected>} />
          <Route path="/admin" element={<Protected role="admin" section="dashboard"><AdminDashboard /></Protected>} />
          <Route path="/admin/controllo" element={<Protected role="admin" section="control"><AdminControl /></Protected>} />
          <Route path="/admin/stazioni" element={<Protected role="admin" section="stations"><AdminStations /></Protected>} />
          <Route path="/admin/utenti" element={<Protected role="admin" section="users" full><AdminUsers /></Protected>} />
          <Route path="/admin/nessuna" element={<Protected role="admin"><NoSections /></Protected>} />
          <Route path="/admin/mezzi" element={<Navigate to="/admin/flotta" replace />} />
          <Route path="/admin/flotta" element={<Protected role="admin" section="fleet"><FleetPage /></Protected>} />
          <Route path="/stazione/flotta" element={<Protected role="operator" section="fleet"><FleetPage /></Protected>} />
          <Route path="/admin/franchigie" element={<Protected role="admin" section="damages"><DamagesPage /></Protected>} />
          <Route path="/stazione/franchigie" element={<Protected role="operator" section="damages"><DamagesPage /></Protected>} />
          <Route path="/admin/noleggi" element={<Protected role="admin" section="rentals"><RentalsPage /></Protected>} />
          <Route path="/stazione/noleggi" element={<Protected role="operator" section="rentals"><RentalsPage /></Protected>} />
          <Route path="/admin/telepass" element={<Protected role="admin" section="telepass"><TelepassPage /></Protected>} />
          <Route path="/stazione/telepass" element={<Protected role="operator" section="telepass"><TelepassPage /></Protected>} />
          <Route path="/giornale/:token" element={<LogbookPage />} />
          <Route path="/admin/assegnazioni" element={<Protected role="admin" section="assignments"><AssignmentsPage /></Protected>} />
          <Route path="/stazione/assegnazioni" element={<Protected role="operator" section="assignments"><AssignmentsPage /></Protected>} />
          <Route path="/admin/telefoni" element={<Protected role="admin" section="phones"><PhonesPage /></Protected>} />
          <Route path="/stazione/telefoni" element={<Protected role="operator" section="phones"><PhonesPage /></Protected>} />
          <Route path="/admin/richieste" element={<Protected role="admin" section="requests"><RequestsPage /></Protected>} />
          <Route path="/stazione/richieste" element={<Protected role="operator" section="requests"><RequestsPage /></Protected>} />
          <Route path="/admin/documenti" element={<Protected role="admin" section="documents"><DocumentsPage /></Protected>} />
          <Route path="/admin/pedaggi" element={<Protected role="admin" section="tolls"><TollsPage /></Protected>} />
          <Route path="/stazione/pedaggi" element={<Protected role="operator" section="tolls"><TollsPage /></Protected>} />
          <Route path="/admin/gomme" element={<Protected role="admin" section="tyres"><TyresPage /></Protected>} />
          <Route path="/stazione/gomme" element={<Protected role="operator" section="tyres"><TyresPage /></Protected>} />
          <Route path="/admin/forecast" element={<Protected role="admin" section="forecast"><ForecastPage /></Protected>} />
          <Route path="/stazione/forecast" element={<Protected role="operator" section="forecast"><ForecastPage /></Protected>} />
          <Route path="/admin/moduli" element={<Protected role="admin" section="forms"><FormsPage /></Protected>} />
          <Route path="/stazione/moduli" element={<Protected role="operator" section="forms"><FormsPage /></Protected>} />
          <Route path="/admin/lavaggi" element={<Protected role="admin" section="washes"><WashesPage /></Protected>} />
          <Route path="/stazione/lavaggi" element={<Protected role="operator" section="washes"><WashesPage /></Protected>} />
          <Route path="/admin/adblue" element={<Protected role="admin" section="adblue"><AdBluePage /></Protected>} />
          <Route path="/stazione/adblue" element={<Protected role="operator" section="adblue"><AdBluePage /></Protected>} />
          <Route path="/modulo/:token" element={<PublicFormPage />} />
          <Route path="/stazione/documenti" element={<Protected role="operator" section="documents"><DocumentsPage />
          </Protected>} />
          <Route path="/stazione/nessuna" element={<Protected role="operator"><NoSections /></Protected>} />
          <Route path="/admin/assistente" element={<Protected role="admin" section="ai" full><AiAssistantPage /></Protected>} />
          <Route path="/admin/importa" element={<Protected role="admin" section="import" full><AdminImport /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
